import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from 'react'
import { getAccessToken, logoutUser } from '../api/authService'
import { getOwnUser } from '../api/userService'
import { useToast } from './ToastContext'
// Define the authentication status enum
export enum AuthStatus {
  LOADING = 'loading',
  AUTHENTICATED = 'authenticated',
  UNAUTHENTICATED = 'unauthenticated',
}

// Define the user type. Access is decided by permission ids (never role names);
// roles are kept only for display.
export interface User {
  email: string
  firstName: string
  lastName: string
  permissionIds: number[]
  roles: string[]
}

// Define the context type
export interface AuthContextType {
  user: User | null
  status: AuthStatus
  login: (code: string) => void
  logout: () => void
}

// Create the context
const AuthContext = createContext<AuthContextType | null>(null)

// Create the Auth Provider component
type AuthProviderProps = {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [status, setStatus] = useState<AuthStatus>(AuthStatus.LOADING)
  const { showError } = useToast()

  const login = async (code: string): Promise<void> => {
    try {
      setStatus(AuthStatus.LOADING)
      await getAccessToken(code)

      const newUser = await getOwnUser()
      setUser(newUser)

      setStatus(AuthStatus.AUTHENTICATED)
    } catch (error) {
      console.error('Error during authorisation:', error)
      setStatus(AuthStatus.UNAUTHENTICATED)
      throw error
    }
  }

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = await getOwnUser()
        setUser(user)
        setStatus(AuthStatus.AUTHENTICATED)
      } catch (error) {
        setStatus(AuthStatus.UNAUTHENTICATED)
        // A 401 just means "not signed in yet" (expected). Anything else — a server
        // error or an unreachable backend — is a real failure worth signalling.
        const httpStatus = (error as { response?: { status?: number } })?.response
          ?.status
        if (httpStatus !== 401) {
          showError('Could not reach the server. Please try again.')
        }
      }
    }
    checkAuth()
  }, [showError])
  const logout = async (): Promise<void> => {
    setStatus(AuthStatus.LOADING)
    try {
      await logoutUser()
    } catch (error) {
      // Non-fatal: the server call failed, but still clear the local session so the
      // user is signed out on this device.
      console.error('Error during logout:', error)
    }
    setUser(null)
    setStatus(AuthStatus.UNAUTHENTICATED)
  }
  const value = {
    user,
    status,
    login,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Create the useAuth hook
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)

  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
